import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

interface User {
    id: string;
    email: string;
    name: string;
    orgId: string;
    role: string;
    companyName?: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    loading: boolean;
    login: (token: string, user: User) => void;
    logout: () => void;
    refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Initial Fetch
        if (token) {
            fetchMe();
        } else {
            setLoading(false);
        }
    }, []);

    const fetchMe = async () => {
        try {
            // Using centralized API client
            const response = await api.auth.me() as any; // Cast as any because response structure might differ
            setUser(response.data);
        } catch (error) {
            console.error('Fetch me failed', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = (newToken: string, newUser: User) => {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(newUser);
    };

    const logout = async () => {
        try {
            await api.auth.logout();
        } catch (e) {
            // Ignore error
        }
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const refreshSession = async (): Promise<boolean> => {
        try {
            // api.post handles the request. 
            // Note: The api client itself has an intricate refresh loop. 
            // We might just trust the client's interceptor instead of manual refresh here.
            // But preserving logic:
            const response = await api.auth.refresh();
            if (response.success && response.token) {
                login(response.token, user!);
                return true;
            }
        } catch (e) {
            console.error('Refresh failed', e);
        }
        return false;
    };

    return (
        <AuthContext.Provider value={{ user, token, isAuthenticated: !!user, loading, login, logout, refreshSession }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
